import { curry, isArray, isPlainObject } from 'lodash/fp';

/**
 * Recursively removes specified properties from object.
 * @param {string[]|function} paths to omit in result
 * @param {object} object input
 */
export const omitDeep = curry((predicate, object) => {
	return isPlainObject(object)
		? Object.entries(object).reduce((newObject, [key, value]) => {
				const isPropertyOmitted = isArray(predicate)
					? predicate.includes(key)
					: predicate(value, key);

				if (isPropertyOmitted) {
					return newObject;
				}

				if (isArray(value)) {
					// eslint-disable-next-line no-param-reassign
					newObject[key] = value.map((item) =>
						isPlainObject(item) ? omitDeep(predicate, item) : item
					);
					return newObject;
				}

				if (isPlainObject(value)) {
					// eslint-disable-next-line no-param-reassign
					newObject[key] = omitDeep(predicate, value);
					return newObject;
				}

				// eslint-disable-next-line no-param-reassign
				newObject[key] = value;

				return newObject;
		  }, {})
		: object;
});

/**
 * Renames keys in object according to renameDict, if renameDict points to falsy
 * value, the key is omitted in result.
 * @param { { [sourceKey: string]: string } } renameDict dictionary mapping
 * original keys to rename keys or to undefined for omitting,
 * @param { {} } sourceObj object with keys to be renamed/omitted,
 * @return {{}}
 */
export const renameKeys = curry((renameDict, sourceObj) => {
	return Object.keys(sourceObj).reduce((targetObj, originalKey) => {
		if (originalKey in renameDict) {
			const renamedKey = renameDict[originalKey];
			// eslint-disable-next-line no-param-reassign
			if (renamedKey) targetObj[renamedKey] = sourceObj[originalKey];
		} else {
			// eslint-disable-next-line no-param-reassign
			targetObj[originalKey] = sourceObj[originalKey];
		}
		return targetObj;
	}, {});
});
